
import React, { useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate, useLocation } from "react-router-dom";
import { apiDelete, apiGet, apiPost, apiPut } from "../../utils/api";
import { toast } from "../../utils/toast";
import { Eye, EyeOff, Clock, AlertCircle, RefreshCw, Search } from "lucide-react";
import { getRole } from "../../utils/authService";
import "./role-accounts.css";

// ---------------------------------------------------------------------------
// My Profile view — shown to non-admin/non-client roles (employee, manager,
// analytics_viewer, ceo, cfo, etc.)
// ---------------------------------------------------------------------------
const ROLE_LABELS = {
  employee: "Employee",
  manager: "Manager",
  analytics_viewer: "Analytics Viewer",
  ceo: "CEO",
  cfo: "CFO",
  client: "Tenant Admin",
  admin: "System Admin",
};

function ProfileField({ label, value }) {
  if (!value) return null;
  return (
    <div>
      <div className="ra-profile-field-label">{label}</div>
      <div className="ra-profile-field-value">{value}</div>
    </div>
  );
}

function initialsFor(name) {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export function MyProfileView({ isModal = false } = {}) {
  const navigate = useNavigate();
  // /profile is the dedicated TopNav "Profile" link (App.jsx) — someone who deliberately
  // opened it wants to see their account details, not the pending-setup nudge. /role-accounts
  // is the other route this same component renders from: the default landing target
  // getDefaultRouteByRole() sends a pending employee/manager to, where the nudge belongs.
  // The TopNav "Profile" dropdown item now opens this in a modal instead of navigating to
  // /profile (see topnav.jsx) — isModal covers that case the same way the route check does.
  const location = useLocation();
  const isDedicatedProfilePage = isModal || location.pathname === "/profile";
  const wrapperClassName = isModal ? "role-accounts-page role-accounts-modal" : "role-accounts-page";
  const [profile, setProfile] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    let active = true;
    const meReq = apiGet("/api/auth/me").then((r) => r.json());
    const allReqs = [meReq];

    Promise.all(allReqs)
      .then(([me]) => {
        if (!active) return;
        setProfile(me);
      })
      .catch(() => { if (active) setError("Could not load profile."); })
      .finally(() => { if (active) setLoading(false); });

    return () => { active = false; };
  }, []);

  // No title here on purpose — until profile loads we don't yet know whether this will be
  // the "My Profile" or the pending-banner state, and committing to either one first just
  // flashes the wrong title for a moment before swapping to the right one.
  if (loading) return (
    <div className={wrapperClassName}>
      <div className="ra-table-card ra-profile-card">Loading...</div>
    </div>
  );

  if (error) return (
    <div className={wrapperClassName}>
      <div className="ra-table-card ra-profile-card" style={{ color: "#dc2626" }}>{error}</div>
    </div>
  );

  const role = profile?.role || getRole() || "—";
  const roleLabel = ROLE_LABELS[role] || role;
  const displayRole = profile?.displayRole || "";
  const tenantName = profile?.tenantId?.name || sessionStorage.getItem("tenantName") || "";
  const needsRoleAssignment = !isDedicatedProfilePage && profile?.ssoRoleAssigned === false;
  const needsAllocation = !isDedicatedProfilePage && !needsRoleAssignment && profile?.ssoNeedsAllocation === true;

  const isPending = needsRoleAssignment || needsAllocation;

  // No outer page title while something's pending — the empty-state card below already
  // carries its own heading, so a second, generic "Dashboard" title above it was redundant.
  // In modal mode there's no title at all here — the modal's own header ("Profile" + close
  // button, see topnav.jsx) already is one.
  return (
    <div className={isPending ? `${wrapperClassName} ra-page-centered` : wrapperClassName}>
      {!isModal && !isPending && (
        <div className="ra-page-head ra-page-head-narrow">
          <div className="ra-page-head-meta">
            <h1>My Profile</h1>
            <p>Your account details and role information.</p>
          </div>
        </div>
      )}

      {needsRoleAssignment && (
        <div className="ra-empty-state">
          <div className="ra-empty-state-icon info"><AlertCircle size={26} /></div>
          <h2>No role assigned yet</h2>
          <p>Your organization's admin hasn't assigned you a role yet — contact them to get access.</p>
        </div>
      )}

      {needsAllocation && (
        <div className="ra-empty-state">
          <div className="ra-empty-state-icon pending"><Clock size={26} /></div>
          <h2>Waiting on device/team assignment</h2>
          <p>
            Your admin still needs to link you to a device or team. You can download and install
            the agent now to get ready in the meantime.
          </p>
          <button type="button" onClick={() => navigate("/download")} className="ra-primary-btn">
            Go to Download page
          </button>
        </div>
      )}

      {/* Account card — on /profile this always shows (that's the whole point of a dedicated
          profile page); on /role-accounts it's held back while something's pending so that
          state reads as just the empty-state card, not empty-state+profile mixed together. */}
      {(isDedicatedProfilePage || !isPending) && (
        <div className="ra-table-card ra-profile-card">
          <div className="ra-profile-header">
            <div className="ra-profile-avatar">{initialsFor(profile?.name)}</div>
            <div className="ra-profile-header-text">
              <strong>{profile?.name || "—"}</strong>
              <span>{profile?.email || "—"}</span>
            </div>
          </div>

          <div className="ra-profile-grid">
            <div>
              <div className="ra-profile-field-label">System Role</div>
              {profile?.ssoRoleAssigned === false ? (
                <span className="ra-role-pill muted">No role assigned</span>
              ) : (
                <span className="ra-role-pill">{displayRole || roleLabel}</span>
              )}
            </div>
            {tenantName && <ProfileField label="Organization" value={tenantName} />}
            {profile?.agentId && role === "employee" && <ProfileField label="Device (Agent ID)" value={profile.agentId} />}
          </div>
        </div>
      )}

    </div>
  );
}

const FALLBACK_VIEW_OPTIONS = [
  { id: "devices", label: "Devices" },
  { id: "monitoring", label: "Monitoring" },
  { id: "executive_dashboard", label: "CEO Dashboard" },
  { id: "cfo_dashboard", label: "CFO Dashboard" },
  { id: "employee_dashboard", label: "Employee Dashboard" },
  { id: "manager_dashboard", label: "Manager Dashboard" },
  { id: "visun_ai", label: "Visun AI" },
  { id: "productivity_config", label: "Configuration Settings" },
  { id: "billing", label: "Plans & Billing" },
  { id: "download", label: "Download" },
  { id: "visualizer", label: "Visualizer" },
  { id: "guide", label: "Guide" },
];

const FALLBACK_VIEW_LABELS = Object.fromEntries(FALLBACK_VIEW_OPTIONS.map((item) => [item.id, item.label]));
const normalize = (items) => Array.from(new Set(Array.isArray(items) ? items : []));
const getCombinedViews = (templates) =>
  Array.from(new Set((templates || []).flatMap((tpl) => tpl.allowedViews || [])));
// A views list matches a scoped role when it contains that role's dashboard view and
// every other view in it is one of that role's allowed extras (e.g. manager + devices).
const SCOPED_ROLE_VIEWS = { employee: ["employee_dashboard"], manager: ["manager_dashboard", "devices", "visun_ai"] };
const matchScopedRoleFromViews = (views) => {
  const list = normalize(views);
  if (list.length === 0) return null;
  for (const role of Object.keys(SCOPED_ROLE_VIEWS)) {
    const roleViews = SCOPED_ROLE_VIEWS[role];
    const allowedSet = new Set(roleViews);
    if (list.includes(roleViews[0]) && list.every((v) => allowedSet.has(v))) return role;
  }
  return null;
};
// The role isn't picked directly anymore — it's derived from which role template(s)
// are selected below, so an admin can never submit a mismatched role/dashboard combo.
const deriveSystemRoleFromViews = (views) => {
  const scopedRole = matchScopedRoleFromViews(views);
  if (scopedRole) return scopedRole;
  if (normalize(views).includes("cfo_dashboard")) return "cfo";
  return "analytics_viewer";
};
const isScopedRoleTemplate = (tpl) => Boolean(matchScopedRoleFromViews(tpl?.allowedViews));
const sameViews = (left, right) => {
  const a = [...normalize(left)].sort();
  const b = [...normalize(right)].sort();
  return JSON.stringify(a) === JSON.stringify(b);
};
const normalizeViewOptions = (items) => {
  if (!Array.isArray(items)) return [];
  const seen = new Set();
  const options = [];
  for (const entry of items) {
    const id = String(entry?.id || "").trim();
    if (!id || seen.has(id)) continue;
    seen.add(id);
    const fallbackLabel = FALLBACK_VIEW_LABELS[id] || id;
    const label = String(entry?.label || fallbackLabel).trim() || fallbackLabel;
    options.push({ id, label });
  }
  return options;
};

function TenantAccountsManager() {
  const [activeSection, setActiveSection] = useState("users");
  const [searchText, setSearchText] = useState("");

  const [accounts, setAccounts] = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);

  const [roleTemplates, setRoleTemplates] = useState([]);
  const [allowedViewOptions, setAllowedViewOptions] = useState(FALLBACK_VIEW_OPTIONS);
  const [loadingRoleTemplates, setLoadingRoleTemplates] = useState(false);
  const [deletingRoleId, setDeletingRoleId] = useState("");
  const [savingRoleId, setSavingRoleId] = useState("");
  const [savingAccountRoleId, setSavingAccountRoleId] = useState("");

  const [showCreateRolePanel, setShowCreateRolePanel] = useState(false);
  const [showCreateUserPanel, setShowCreateUserPanel] = useState(false);

  const [newRoleName, setNewRoleName] = useState("");
  const [newRoleViews, setNewRoleViews] = useState([]);
  const [creatingRole, setCreatingRole] = useState(false);

  const [editingRoleTemplate, setEditingRoleTemplate] = useState(null);
  const [editRoleName, setEditRoleName] = useState("");
  const [editRoleViews, setEditRoleViews] = useState([]);

  const [newAccountName, setNewAccountName] = useState("");
  const [newAccountEmail, setNewAccountEmail] = useState("");
  const [newAccountPassword, setNewAccountPassword] = useState("");
  const [newAccountDepartmentId, setNewAccountDepartmentId] = useState("");
  const [newAccountAgentId, setNewAccountAgentId] = useState("");
  const [departments, setDepartments] = useState([]);
  const [loadingDepartments, setLoadingDepartments] = useState(false);
  const [agents, setAgents] = useState([]);
  const [loadingAgents, setLoadingAgents] = useState(false);
  const [creatingAccount, setCreatingAccount] = useState(false);
  const [showCreatePassword, setShowCreatePassword] = useState(false);
  const [selectedRoleTemplateIds, setSelectedRoleTemplateIds] = useState([]);
  const [newAccountManagedDepartmentIds, setNewAccountManagedDepartmentIds] = useState([]);
  const [newAccountAllTeams, setNewAccountAllTeams] = useState(false);

  const [editingAccountRole, setEditingAccountRole] = useState(null);
  const [selectedAccountRoleTemplateIds, setSelectedAccountRoleTemplateIds] = useState([]);
  const [editAccountPassword, setEditAccountPassword] = useState("");
  const [showEditAccountPassword, setShowEditAccountPassword] = useState(false);
  const [editAccountManagedDepartmentIds, setEditAccountManagedDepartmentIds] = useState([]);
  const [editAccountAllTeams, setEditAccountAllTeams] = useState(false);
  const [editAccountDepartmentId, setEditAccountDepartmentId] = useState("");
  const [editAccountAgentId, setEditAccountAgentId] = useState("");

  const selectableViewOptions = useMemo(
    () => normalizeViewOptions(allowedViewOptions),
    [allowedViewOptions]
  );
  const selectableViewIds = useMemo(
    () => selectableViewOptions.map((view) => view.id),
    [selectableViewOptions]
  );
  const selectableViewIdSet = useMemo(
    () => new Set(selectableViewIds),
    [selectableViewIds]
  );
  const viewLabelMap = useMemo(() => {
    const labelMap = { ...FALLBACK_VIEW_LABELS };
    selectableViewOptions.forEach((view) => {
      labelMap[view.id] = view.label;
    });
    return labelMap;
  }, [selectableViewOptions]);
  const labelForView = (id) => viewLabelMap[id] || id;

  const resetCreateRoleForm = () => {
    setNewRoleName("");
    setNewRoleViews([]);
  };

  const resetCreateUserForm = () => {
    setNewAccountName("");
    setNewAccountEmail("");
    setNewAccountPassword("");
    setNewAccountDepartmentId("");
    setNewAccountAgentId("");
    setSelectedRoleTemplateIds([]);
    setShowCreatePassword(false);
    setNewAccountManagedDepartmentIds([]);
    setNewAccountAllTeams(false);
  };

  const closeCreateRolePanel = () => {
    setShowCreateRolePanel(false);
    resetCreateRoleForm();
  };

  const closeCreateUserPanel = () => {
    setShowCreateUserPanel(false);
    resetCreateUserForm();
  };

  const closeEditRoleTemplate = () => {
    setEditingRoleTemplate(null);
    setEditRoleName("");
    setEditRoleViews([]);
    setSavingRoleId("");
  };

  const closeAccountRoleEditor = () => {
    setEditingAccountRole(null);
    setSelectedAccountRoleTemplateIds([]);
    setEditAccountDepartmentId("");
    setEditAccountAgentId("");
    setSavingAccountRoleId("");
    setEditAccountPassword("");
    setShowEditAccountPassword(false);
    setEditAccountManagedDepartmentIds([]);
    setEditAccountAllTeams(false);
  };

  const toggleTemplateSelection = (templateId, setter) => {
    setter((prev) =>
      prev.includes(templateId) ? prev.filter((id) => id !== templateId) : [...prev, templateId]
    );
  };

  // Employee/Manager templates define the account's derived role on their own —
  // mixing them with other templates would make that derivation ambiguous, so
  // picking one clears any other selection (and vice versa).
  const toggleCreateRoleTemplate = (tpl) => {
    setNewAccountAgentId("");
    setNewAccountManagedDepartmentIds([]);
    setNewAccountAllTeams(false);
    setSelectedRoleTemplateIds((prev) => {
      const isSelected = prev.includes(tpl._id);
      if (isSelected) return prev.filter((id) => id !== tpl._id);
      if (isScopedRoleTemplate(tpl)) return [tpl._id];
      const hasScopedSelected = roleTemplates.some((t) => prev.includes(t._id) && isScopedRoleTemplate(t));
      if (hasScopedSelected) return [tpl._id];
      return [...prev, tpl._id];
    });
  };

  const toggleEditRoleTemplate = (tpl) => {
    setEditAccountAgentId("");
    setEditAccountManagedDepartmentIds([]);
    setEditAccountAllTeams(false);
    setSelectedAccountRoleTemplateIds((prev) => {
      const isSelected = prev.includes(tpl._id);
      if (isSelected) return prev.filter((id) => id !== tpl._id);
      if (isScopedRoleTemplate(tpl)) return [tpl._id];
      const hasScopedSelected = roleTemplates.some((t) => prev.includes(t._id) && isScopedRoleTemplate(t));
      if (hasScopedSelected) return [tpl._id];
      return [...prev, tpl._id];
    });
  };

  useEffect(() => {
    if (!editingAccountRole || derivedEditAccountRole !== "manager" || departments.length === 0) return;
    const managedSet = new Set(editAccountManagedDepartmentIds.map(String));
    setEditAccountAllTeams(departments.every((d) => managedSet.has(String(d._id))));
  }, [editingAccountRole, departments]);

  useEffect(() => {
    const onEscape = (event) => {
      if (event.key !== "Escape") return;
      if (editingAccountRole) return closeAccountRoleEditor();
      if (editingRoleTemplate) return closeEditRoleTemplate();
      if (showCreateUserPanel) return closeCreateUserPanel();
      if (showCreateRolePanel) return closeCreateRolePanel();
    };

    document.addEventListener("keydown", onEscape);
    return () => document.removeEventListener("keydown", onEscape);
  }, [editingAccountRole, editingRoleTemplate, showCreateUserPanel, showCreateRolePanel]);

  useEffect(() => {
    setNewRoleViews((prev) => normalize(prev).filter((id) => selectableViewIdSet.has(id)));
    setEditRoleViews((prev) => normalize(prev).filter((id) => selectableViewIdSet.has(id)));
  }, [selectableViewIdSet]);

  const loadAgents = async () => {
    setLoadingAgents(true);
    try {
      const res = await apiGet("/api/agents");
      const data = await res.json().catch(() => null);
      if (res.ok && Array.isArray(data)) {
        setAgents(data);
      }
    } catch {
      // non-fatal
    } finally {
      setLoadingAgents(false);
    }
  };

  const loadAccounts = async () => {
    setLoadingAccounts(true);
    try {
      const res = await apiGet("/api/users/tenant-accounts");
      const data = await res.json().catch(() => null);
      if (!res.ok || !data?.success) {
        toast.error(data?.message || "Failed to load accounts");
        return;
      }
      setAccounts(Array.isArray(data.users) ? data.users : []);
    } catch {
      toast.error("Failed to load accounts");
    } finally {
      setLoadingAccounts(false);
    }
  };

  const loadRoleTemplates = async () => {
    setLoadingRoleTemplates(true);
    try {
      const res = await apiGet("/api/users/account-roles");
      const data = await res.json().catch(() => null);
      if (!res.ok || !data?.success) {
        toast.error(data?.message || "Failed to load role templates");
        return;
      }

      const nextAllowedViewOptions = Array.isArray(data?.allowedViewOptions)
        ? normalizeViewOptions(data.allowedViewOptions)
        : FALLBACK_VIEW_OPTIONS;
      const allowedViewSet = new Set(nextAllowedViewOptions.map((view) => view.id));

      setAllowedViewOptions(nextAllowedViewOptions);

      const rows = (Array.isArray(data.roles) ? data.roles : []).map((row) => ({
        ...row,
        allowedViews: normalize(row?.allowedViews).filter((id) => allowedViewSet.has(id)),
      }));
      setRoleTemplates(rows);
      setSelectedRoleTemplateIds((prev) => prev.filter((id) => rows.some((row) => row._id === id)));
      setSelectedAccountRoleTemplateIds((prev) =>
        prev.filter((id) => rows.some((row) => row._id === id))
      );
    } catch {
      toast.error("Failed to load role templates");
    } finally {
      setLoadingRoleTemplates(false);
    }
  };

  const loadDepartments = async () => {
    setLoadingDepartments(true);
    try {
      const res = await apiGet("/api/departments");
      const data = await res.json().catch(() => null);
      if (!res.ok || !data?.success) return;
      setDepartments(Array.isArray(data.departments) ? data.departments : []);
    } catch {
      // Non-fatal: the manager-department picker just stays empty.
    } finally {
      setLoadingDepartments(false);
    }
  };

  useEffect(() => {
    loadAccounts();
    loadRoleTemplates();
    loadDepartments();
  }, []);

  const selectedRoleTemplates = useMemo(
    () => roleTemplates.filter((tpl) => selectedRoleTemplateIds.includes(tpl._id)),
    [roleTemplates, selectedRoleTemplateIds]
  );

  const selectedCreateViews = useMemo(() => getCombinedViews(selectedRoleTemplates), [selectedRoleTemplates]);
  const derivedNewAccountRole = useMemo(
    () => deriveSystemRoleFromViews(selectedCreateViews),
    [selectedCreateViews]
  );

  const selectedAccountViews = useMemo(
    () => getCombinedViews(roleTemplates.filter((tpl) => selectedAccountRoleTemplateIds.includes(tpl._id))),
    [roleTemplates, selectedAccountRoleTemplateIds]
  );
  const derivedEditAccountRole = useMemo(
    () => deriveSystemRoleFromViews(selectedAccountViews),
    [selectedAccountViews]
  );

  const filteredAccounts = useMemo(() => {
    const query = searchText.trim().toLowerCase();
    if (!query) return accounts;

    return accounts.filter((account) => {
      const viewLabels = Array.isArray(account.allowedViews)
        ? account.allowedViews.map((id) => labelForView(id)).join(" ")
        : "";
      const text = [account.name, account.email, account.displayRole, account.role, viewLabels]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return text.includes(query);
    });
  }, [accounts, searchText]);

  const formatDate = (isoDate) => {
    if (!isoDate) return "-";
    const date = new Date(isoDate);
    if (Number.isNaN(date.getTime())) return "-";
    return new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "2-digit",
      year: "numeric",
    }).format(date);
  };

  // displayRole can be a comma-joined combination when an account has multiple role
  // templates assigned (e.g. "Tenant Admin, CFO") — the table shows just the first one
  // as a single role label instead of the full combined list.
  const getRoleLabel = (account) => {
    const display = String(account?.displayRole || "").trim();
    if (display) return display.split(",")[0].trim();
    return account?.role || "-";
  };
  const getViewPreview = (views) => (Array.isArray(views) ? views.slice(0, 2).map((id) => labelForView(id)) : []);
  const handleCreateRoleTemplate = async (event) => {
    event.preventDefault();
    const roleName = String(newRoleName || "").trim();
    if (!roleName) return toast.error("Role name is required");

    setCreatingRole(true);
    try {
      const res = await apiPost("/api/users/account-roles", {
        name: roleName,
        allowedViews: normalize(newRoleViews),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok || !data?.success) return toast.error(data?.message || "Failed to create role");

      toast.success("Role created");
      closeCreateRolePanel();
      await loadRoleTemplates();
      await loadAccounts();
    } catch {
      toast.error("Failed to create role");
    } finally {
      setCreatingRole(false);
    }
  };

  const saveRoleTemplate = async () => {
    if (!editingRoleTemplate?._id) return;
    const roleName = String(editRoleName || "").trim();
    if (!roleName) return toast.error("Role name is required");

    setSavingRoleId(editingRoleTemplate._id);
    try {
      const res = await apiPut(`/api/users/account-roles/${editingRoleTemplate._id}`, {
        name: roleName,
        allowedViews: normalize(editRoleViews),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok || !data?.success) return toast.error(data?.message || "Failed to update role");

      toast.success("Role updated");
      await loadRoleTemplates();
      await loadAccounts();
      closeEditRoleTemplate();
    } catch {
      toast.error("Failed to update role");
    } finally {
      setSavingRoleId("");
    }
  };

  const handleDeleteRoleTemplate = async (roleId) => {
    if (!window.confirm("Delete this role template?")) return;

    setDeletingRoleId(roleId);
    try {
      const res = await apiDelete(`/api/users/account-roles/${roleId}`);
      const data = await res.json().catch(() => null);
      if (!res.ok || !data?.success) return toast.error(data?.message || "Failed to delete role");

      toast.success("Role deleted");
      await loadRoleTemplates();
      await loadAccounts();
    } catch {
      toast.error("Failed to delete role");
    } finally {
      setDeletingRoleId("");
    }
  };

  const handleDeleteAccount = async (accountId) => {
    if (!window.confirm("Are you sure you want to delete this user?")) return;

    try {
      const res = await apiDelete(`/api/users/tenant-accounts/${accountId}`);
      const data = await res.json().catch(() => null);
      if (!res.ok || !data?.success) return toast.error(data?.message || "Failed to delete user");

      toast.success("User deleted successfully");
      await loadAccounts();
    } catch {
      toast.error("Failed to delete user");
    }
  };

  // True until an admin has actually looked at and set this account's role/permissions — the
  // role/displayRole it shows until then is just the tenant's bare JIT default, not a real
  // assignment, so the table shouldn't present it as one.
  const needsRoleAssignment = (account) => account.ssoRoleAssigned === false;

  // True for a role-assigned SSO employee/manager still missing the Agent/departmentId those
  // roles require — see User.js's ssoNeedsAllocation.
  const needsAllocation = (account) => account.ssoNeedsAllocation === true;


  const handleCreateAccount = async (event) => {
    event.preventDefault();

    const name = String(newAccountName || "").trim();
    const email = String(newAccountEmail || "").trim().toLowerCase();
    const password = String(newAccountPassword || "");
    const selectedTemplates = roleTemplates.filter((tpl) => selectedRoleTemplateIds.includes(tpl._id));
    const combinedViews = getCombinedViews(selectedTemplates);
    const systemRole = deriveSystemRoleFromViews(combinedViews);

    if (!name) return toast.error("User name is required");
    if (selectedTemplates.length === 0) return toast.error("Please select at least one role");
    if (!email || !password) return toast.error("Email and password are required");
    if (password.length < 8) return toast.error("Password must be at least 8 characters");
    {
      const hasLower = /[a-z]/.test(password);
      const hasUpper = /[A-Z]/.test(password);
      const hasDigit = /[0-9]/.test(password);
      const hasSpecial = /[^a-zA-Z0-9]/.test(password);
      const classCount = [hasLower, hasUpper, hasDigit, hasSpecial].filter(Boolean).length;
      if (classCount < 3) {
        return toast.error(
          "Password must include at least 3 of: uppercase, lowercase, numbers, and special characters"
        );
      }
    }
    if (systemRole === "manager" && !newAccountAllTeams && newAccountManagedDepartmentIds.length === 0) {
      return toast.error("Please select at least one team for this manager");
    }
    if (systemRole === "employee" && !String(newAccountAgentId || "").trim()) {
      return toast.error("Agent ID is required for Employee accounts");
    }

    setCreatingAccount(true);
    try {
      const combinedRoleName = selectedTemplates.map((tpl) => tpl.name).join(", ");
      const payload = {
        name,
        email,
        password,
        role: systemRole,
        roleTemplateIds: selectedRoleTemplateIds,
        displayRole: combinedRoleName,
        allowedViews: combinedViews,
        managedDepartmentIds: systemRole === "manager"
          ? (newAccountAllTeams ? departments.map((d) => d._id) : newAccountManagedDepartmentIds)
          : (combinedViews.includes("manager_dashboard") ? newAccountManagedDepartmentIds : []),
      };
      if (systemRole === "manager") {
        const firstTeam = newAccountAllTeams ? departments[0]?._id : newAccountManagedDepartmentIds[0];
        payload.departmentId = String(firstTeam || "");
      }
      if (systemRole === "employee") payload.agentId = String(newAccountAgentId || "").trim();

      const res = await apiPost("/api/users/create-role-account", payload);
      const data = await res.json().catch(() => null);
      if (!res.ok) return toast.error(data?.message || "Failed to create account");

      toast.success("Account created");
      closeCreateUserPanel();
      await loadAccounts();
    } catch {
      toast.error("Failed to create account");
    } finally {
      setCreatingAccount(false);
    }
  };

  const openEditRoleTemplate = (template) => {
    setEditingRoleTemplate(template);
    setEditRoleName(String(template?.name || ""));
    setEditRoleViews(normalize(template?.allowedViews).filter((id) => selectableViewIdSet.has(id)));
  };

  const openAccountRoleEditor = (account) => {
    const templateIds = Array.isArray(account?.accountRoleTemplateIds)
      ? account.accountRoleTemplateIds.map((id) => String(id))
      : [];
    const roleNames = String(account?.displayRole || "")
      .split(",")
      .map((name) => name.trim())
      .filter(Boolean);

    setEditingAccountRole(account);
    setEditAccountPassword("");
    setShowEditAccountPassword(false);
    setEditAccountManagedDepartmentIds(
      Array.isArray(account?.managedDepartmentIds) ? account.managedDepartmentIds.map((id) => String(id)) : []
    );
    setEditAccountDepartmentId(account?.departmentId ? String(account.departmentId) : "");
    setEditAccountAgentId(account?.agentId ? String(account.agentId) : "");
    loadDepartments();
    loadAgents();

    if (templateIds.length > 0) {
      setSelectedAccountRoleTemplateIds(
        roleTemplates
          .filter((tpl) => templateIds.includes(String(tpl._id)))
          .map((tpl) => tpl._id)
      );
      return;
    }

    const nameMatched = roleTemplates.filter((tpl) => roleNames.includes(tpl.name));
    if (nameMatched.length > 0) {
      setSelectedAccountRoleTemplateIds(nameMatched.map((tpl) => tpl._id));
      return;
    }

    // No template linkage and no displayRole to parse — true for an SSO-provisioned account,
    // which gets a raw role string directly (KAN-143) and never goes through the template
    // system at all. Fall back to the template matching the account's actual role, by name,
    // so the admin isn't forced to re-declare a role decision that's already been made — they
    // should only need to fill in what's actually missing (e.g. a manager's team).
    const roleLabel = ROLE_LABELS[account?.role];
    setSelectedAccountRoleTemplateIds(
      roleTemplates.filter((tpl) => tpl.name === roleLabel).map((tpl) => tpl._id)
    );
  };

  const saveAccountRole = async () => {
    if (!editingAccountRole?._id) return;

    const selectedTemplates = roleTemplates.filter((tpl) => selectedAccountRoleTemplateIds.includes(tpl._id));
    const combinedViews = getCombinedViews(selectedTemplates);
    const systemRole = deriveSystemRoleFromViews(combinedViews);

    if (selectedTemplates.length === 0) return toast.error("Please select at least one role");
    if (systemRole === "manager" && !editAccountAllTeams && editAccountManagedDepartmentIds.length === 0) {
      return toast.error("Please select at least one team for this manager");
    }
    if (systemRole === "employee" && !String(editAccountAgentId || "").trim()) {
      return toast.error("Agent ID is required for Employee accounts");
    }

    const newPassword = String(editAccountPassword || "").trim();
    if (newPassword) {
      if (newPassword.length < 8) return toast.error("Password must be at least 8 characters");
      const hasLower = /[a-z]/.test(newPassword);
      const hasUpper = /[A-Z]/.test(newPassword);
      const hasDigit = /[0-9]/.test(newPassword);
      const hasSpecial = /[^a-zA-Z0-9]/.test(newPassword);
      const classCount = [hasLower, hasUpper, hasDigit, hasSpecial].filter(Boolean).length;
      if (classCount < 3) {
        return toast.error(
          "Password must include at least 3 of: uppercase, lowercase, numbers, and special characters"
        );
      }
    }

    setSavingAccountRoleId(editingAccountRole._id);
    try {
      const combinedRoleName = selectedTemplates.map((tpl) => tpl.name).join(", ");

      const payload = {
        role: systemRole,
        displayRole: combinedRoleName,
        allowedViews: combinedViews,
        roleTemplateIds: selectedAccountRoleTemplateIds,
        managedDepartmentIds: combinedViews.includes("manager_dashboard") ? editAccountManagedDepartmentIds : [],
      };
      if (systemRole === "manager") {
        const firstTeam = editAccountAllTeams ? departments[0]?._id : editAccountManagedDepartmentIds[0];
        payload.departmentId = String(firstTeam || "");
        payload.managedDepartmentIds = editAccountAllTeams ? departments.map((d) => d._id) : editAccountManagedDepartmentIds;
      }
      if (systemRole === "employee") payload.agentId = String(editAccountAgentId || "").trim();
      if (newPassword) payload.password = newPassword;

      const res = await apiPut(`/api/users/tenant-accounts/${editingAccountRole._id}`, payload);
      const data = await res.json().catch(() => null);
      if (!res.ok || !data?.success) return toast.error(data?.message || "Failed to update account");

      setAccounts((prev) => prev.map((acc) => (
        acc._id === editingAccountRole._id
          ? {
              ...acc,
              role: systemRole,
              displayRole: combinedRoleName,
              allowedViews: combinedViews,
              accountRoleTemplateIds: selectedAccountRoleTemplateIds,
              managedDepartmentIds: payload.managedDepartmentIds,
              departmentId: data.user?.departmentId ?? acc.departmentId,
              agentId: data.user?.agentId ?? acc.agentId,
              requiresPasswordReset: newPassword ? false : acc.requiresPasswordReset,
            }
          : acc
      )));
      toast.success(newPassword ? "User role and password updated" : "Account updated");
      closeAccountRoleEditor();
    } catch {
      toast.error("Failed to update account");
    } finally {
      setSavingAccountRoleId("");
    }
  };

  const roleEditUnchanged = editingRoleTemplate
    ? String(editRoleName || "").trim() === String(editingRoleTemplate.name || "").trim()
    && sameViews(editRoleViews, editingRoleTemplate.allowedViews || [])
    : true;
  return (
    <div className="role-accounts-page">
      <div className="ra-page-head">
        <div className="ra-page-head-meta">
          <h1>Manage Accounts</h1>
          <p>Manage users, roles, and role-based view access in one place.</p>
        </div>

        <div className="ra-page-head-actions">
          <div className="ra-section-switch">
            <button type="button" className={`ra-secondary-btn ra-section-btn ${activeSection === "users" ? "active" : ""}`} onClick={() => setActiveSection("users")}>Users</button>
            <button type="button" className={`ra-secondary-btn ra-section-btn ${activeSection === "roles" ? "active" : ""}`} onClick={() => setActiveSection("roles")}>Roles</button>
          </div>
        </div>
      </div>

      {activeSection === "roles" && (
        <div className="ra-table-card">
          <div className="ra-list-header">
            <h2>Roles ({roleTemplates.length})</h2>
            <div className="ra-top-actions">
              <button type="button" className="gc-icon-btn" onClick={loadRoleTemplates} disabled={loadingRoleTemplates} data-tip="Refresh" aria-label="Refresh">
                <RefreshCw size={14} className={loadingRoleTemplates ? "animate-spin" : ""} />
              </button>
              <button
                type="button"
                className="ra-primary-btn"
                onClick={() => {
                  resetCreateRoleForm();
                  setShowCreateRolePanel(true);
                }}
              >
                Create Role
              </button>
            </div>
          </div>

          <div className="ra-role-list">
            <div className="ra-template-head">Role Templates</div>
            {loadingRoleTemplates && roleTemplates.length === 0 && (
              <div className="ra-empty">Loading default role templates...</div>
            )}
            {!loadingRoleTemplates && roleTemplates.length === 0 && (
              <div className="ra-empty">Default role templates are being set up. Refresh the page in a moment.</div>
            )}
            {roleTemplates.map((tpl) => (
              <div key={tpl._id} className="ra-role-list-item">
                <div><strong>{tpl.name}</strong><span>{(tpl.allowedViews || []).length} views</span></div>
                <div className="ra-inline-actions">
                  <button type="button" className="ra-secondary-btn ra-delete-btn" onClick={() => openEditRoleTemplate(tpl)}>Edit</button>
                  <button type="button" className="ra-secondary-btn ra-delete-btn" onClick={() => handleDeleteRoleTemplate(tpl._id)} disabled={deletingRoleId === tpl._id}>{deletingRoleId === tpl._id ? "Deleting..." : "Delete"}</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeSection === "users" && (
        <div className="ra-table-card">
          <div className="ra-list-header">
            <h2>Accounts ({filteredAccounts.length})</h2>
            <div className="ra-top-actions">
              <div className="gc-filter-search ra-search-wrap">
                <Search size={13} />
                <input className="ra-search-input" value={searchText} onChange={(event) => setSearchText(event.target.value)} placeholder="Search name, email, role, views..." />
              </div>
              <button type="button" className="gc-icon-btn" onClick={loadAccounts} disabled={loadingAccounts} data-tip="Refresh" aria-label="Refresh">
                <RefreshCw size={14} className={loadingAccounts ? "animate-spin" : ""} />
              </button>
              <button
                type="button"
                className="ra-primary-btn"
                onClick={() => {
                  resetCreateUserForm();
                  setShowCreateUserPanel(true);
                  loadDepartments();
                  loadAgents();
                }}
              >
                Create User
              </button>
            </div>
          </div>

          <div className="ra-table-wrap">
            <table className="ra-table">
              <thead><tr><th>Name</th><th>Email</th><th>Role</th><th>Status</th><th>Role Views</th><th>Updated</th><th>Action</th></tr></thead>
              <tbody>
                {filteredAccounts.map((account) => (
                  <tr key={account._id} className={account.isTenantOwner ? "ra-owner-row" : undefined}>
                    <td>{account.name || "-"}</td>
                    <td>{account.email || "-"}</td>
                    <td>
                      <div className="ra-role-cell">
                        {needsRoleAssignment(account) ? (
                          <span className="ra-role-title" style={{ color: "var(--text-secondary, #6b7280)", fontStyle: "italic" }}>No role assigned</span>
                        ) : (
                          <>
                            <span className="ra-role-title">{getRoleLabel(account)}</span>
                            <span className="ra-role-sub">{account.role || "-"}</span>
                          </>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="ra-status-cell">
                        <span className={`ra-status-pill ${account.isActive ? "active" : "inactive"}`}>{account.isActive ? "Active" : "Inactive"}</span>
                        {account.isTenantOwner && (
                          <span className="ra-status-pill active" title="This is the account that created the tenant. It cannot be edited or deleted here.">Tenant Owner</span>
                        )}
                        {account.isDownloadAccount && (
                          <span className="ra-status-pill active" title="Seeded login used only to download the agent installer. Only a platform admin can edit or delete it.">Download Login</span>
                        )}
                        {account.requiresPasswordReset && (
                          <span className="ra-status-pill inactive" title="This account's password no longer meets the security policy. Reset it to a stronger one.">Weak password</span>
                        )}
                        {needsAllocation(account) && (
                          <span className="ra-status-pill inactive" title={`Approved, but needs a device (employee) or department (manager) linked before they can access their dashboard. Use ${account.role === "employee" ? "Edit Role's agent selector" : "Edit Role's department selector"} to complete it.`}>
                            {account.role === "employee" ? "Device allocation pending" : "Team allocation pending"}
                          </span>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="ra-views-cell">
                        <div className="ra-views-text">
                          {account.isTenantOwner
                            ? "All views (tenant owner)"
                            : account.isDownloadAccount
                              ? "Download page only"
                              : ["employee", "manager"].includes(account.role)
                                ? "Role-based access (built-in dashboard, no view list)"
                                : Array.isArray(account.allowedViews) && account.allowedViews.length > 0
                                  ? <div className="ra-view-chips">{getViewPreview(account.allowedViews).map((label) => <span key={`${account._id}-${label}`} className="ra-view-chip">{label}</span>)}{account.allowedViews.length > 2 && <span className="ra-view-chip muted">+{account.allowedViews.length - 2} more</span>}</div>
                                  : "No views assigned"}
                        </div>
                      </div>
                    </td>
                    <td>{formatDate(account.updatedAt || account.createdAt)}</td>
                    <td>
                      {account.isTenantOwner ? (
                        <span className="ra-role-sub">Tenant Owner — read only</span>
                      ) : account.isDownloadAccount ? (
                        <span className="ra-role-sub">Download login — read only</span>
                      ) : (
                      <div className="ra-inline-actions">
                        <button type="button" className="ra-secondary-btn ra-delete-btn" onClick={() => openAccountRoleEditor(account)}>Edit Role</button>
                        <button type="button" className="ra-secondary-btn ra-delete-btn" onClick={() => handleDeleteAccount(account._id)}>Delete</button>
                      </div>
                      )}
                    </td>
                  </tr>
                ))}
                {filteredAccounts.length === 0 && <tr><td colSpan={7} className="ra-empty">No accounts found.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {editingRoleTemplate && (
        <div className="ra-modal-overlay" onClick={closeEditRoleTemplate}>
          <div className="ra-modal ra-modal-medium" onClick={(event) => event.stopPropagation()}>
            <div className="ra-modal-header">
              <div><h3>Edit Role</h3><p>Update role name and allowed views.</p></div>
              <button type="button" className="ra-modal-close" onClick={closeEditRoleTemplate} aria-label="Close edit role modal">×</button>
            </div>

            <div className="ra-modal-scroll">
              <div className="ra-form-stack ra-modal-body-pad">
                <label>Role Name</label>
                <input value={editRoleName} onChange={(event) => setEditRoleName(event.target.value)} placeholder="Role name" />
              </div>

              <div className="ra-views-toolbar">
                <strong>Allowed Views</strong>
                <div className="ra-inline-actions">
                  <button type="button" className="ra-link-btn" onClick={() => setEditRoleViews(selectableViewIds)}>Select all</button>
                  <button type="button" className="ra-link-btn" onClick={() => setEditRoleViews([])}>Clear</button>
                </div>
              </div>

              <div className="ra-views-options">
                {selectableViewOptions.map((view) => (
                  <label key={`edit-role-${view.id}`} className="ra-option-row">
                    <input type="checkbox" checked={editRoleViews.includes(view.id)} onChange={() => toggleTemplateSelection(view.id, setEditRoleViews)} />
                    <span>{view.label}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="ra-views-actions">
              <button type="button" className="ra-secondary-btn" onClick={closeEditRoleTemplate}>Cancel</button>
              <button type="button" className="ra-primary-btn" onClick={saveRoleTemplate} disabled={savingRoleId === editingRoleTemplate._id || roleEditUnchanged}>{savingRoleId === editingRoleTemplate._id ? "Saving..." : "Save Role"}</button>
            </div>
          </div>
        </div>
      )}

      {showCreateRolePanel && (
        <div className="ra-modal-overlay" onClick={closeCreateRolePanel}>
          <div className="ra-modal ra-modal-medium" onClick={(event) => event.stopPropagation()}>
            <div className="ra-modal-header">
              <div><h3>Create Role</h3><p>Define role name and allowed views.</p></div>
              <button type="button" className="ra-modal-close" onClick={closeCreateRolePanel} aria-label="Close create role modal">×</button>
            </div>

            <form onSubmit={handleCreateRoleTemplate} className="ra-form-stack">
              <div className="ra-modal-scroll ra-modal-body-pad">
                <label>Role Name</label>
                <input value={newRoleName} onChange={(event) => setNewRoleName(event.target.value)} placeholder="Ex: HR Manager" />
                <div className="ra-create-role-head">
                  <label>Allowed Views</label>
                  <div className="ra-inline-actions">
                    <button type="button" className="ra-link-btn" onClick={() => setNewRoleViews(selectableViewIds)}>Select all</button>
                    <button type="button" className="ra-link-btn" onClick={() => setNewRoleViews([])}>Clear</button>
                  </div>
                </div>
                <div className="ra-views-options compact ra-create-role-options">
                  {selectableViewOptions.map((view) => (
                    <label key={`new-role-modal-${view.id}`} className="ra-option-row">
                      <input type="checkbox" checked={newRoleViews.includes(view.id)} onChange={() => toggleTemplateSelection(view.id, setNewRoleViews)} />
                      <span>{view.label}</span>
                    </label>
                  ))}
                </div>
                <div className="ra-helper-text">Selected: {newRoleViews.length} view(s)</div>
              </div>
              <div className="ra-views-actions">
                <button type="button" className="ra-secondary-btn" onClick={closeCreateRolePanel}>Cancel</button>
                <button type="submit" className="ra-primary-btn" disabled={creatingRole}>{creatingRole ? "Creating..." : "Create Role"}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showCreateUserPanel && (
        <div className="ra-modal-overlay" onClick={closeCreateUserPanel}>
          <div className="ra-modal ra-modal-wide" onClick={(event) => event.stopPropagation()}>
            <div className="ra-modal-header">
              <div><h3>Create User</h3><p>Create a user and map one or multiple roles.</p></div>
              <button type="button" className="ra-modal-close" onClick={closeCreateUserPanel} aria-label="Close create user modal">×</button>
            </div>

            <form onSubmit={handleCreateAccount} className="ra-form-stack ra-create-user-panel">
              <div className="ra-modal-scroll ra-modal-body-pad">
                <div className="ra-create-user-block ra-create-user-fields">
                  <div className="ra-form-grid">
                    <div>
                      <label>Name</label>
                      <input
                        value={newAccountName}
                        onChange={(event) => setNewAccountName(event.target.value)}
                        placeholder="Ex: Rahul"
                        required
                      />
                    </div>
                    <div>
                      <label>Email</label>
                      <input
                        type="email"
                        value={newAccountEmail}
                        onChange={(event) => setNewAccountEmail(event.target.value)}
                        placeholder="user@company.com"
                        required
                      />
                    </div>
                    <div className="ra-form-col-span-2">
                      <label>Password</label>
                      <div className="ra-password-input-wrapper">
                        <input
                          type={showCreatePassword ? "text" : "password"}
                          value={newAccountPassword}
                          onChange={(event) => setNewAccountPassword(event.target.value)}
                          placeholder="Minimum 8 characters"
                          minLength={8}
                          required
                        />
                        <button
                          type="button"
                          className="ra-password-toggle-btn"
                          onClick={() => setShowCreatePassword(!showCreatePassword)}
                          tabIndex={-1}
                        >
                          {showCreatePassword ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                      </div>
                      <div className="ra-helper-text">
                        At least 8 characters with a mix of uppercase, lowercase, numbers, and symbols.
                      </div>
                    </div>
                  </div>
                </div>

                <div className="ra-create-user-block ra-create-user-rolemap">
                  <label className="ra-section-label">Role (controls dashboard &amp; data access)</label>
                  <div className="ra-helper-text">
                    Required. Select one or more role templates. Picking "Employee" or "Manager" grants that
                    dashboard exclusively; any other selection grants the combined views from the chosen templates.
                  </div>

                  <div className="ra-role-select-row ra-role-select-stack">
                    <details className="ra-multi-select">
                      <summary>
                        {selectedRoleTemplates.length > 0
                          ? `${selectedRoleTemplates.length} role(s) selected`
                          : "Select roles"}
                      </summary>
                      <div className="ra-multi-select-options">
                        {roleTemplates.map((tpl) => (
                          <label key={`create-role-modal-${tpl._id}`} className="ra-option-row">
                            <input
                              type="checkbox"
                              checked={selectedRoleTemplateIds.includes(tpl._id)}
                              onChange={() => toggleCreateRoleTemplate(tpl)}
                              disabled={loadingRoleTemplates}
                            />
                            <span>{tpl.name}</span>
                          </label>
                        ))}
                      </div>
                    </details>
                    <button
                      type="button"
                      className="ra-secondary-btn"
                      onClick={loadRoleTemplates}
                      disabled={loadingRoleTemplates}
                    >
                      {loadingRoleTemplates ? "Loading..." : "Reload"}
                    </button>
                  </div>

                  {roleTemplates.length === 0 && (
                    <div className="ra-empty">No roles available. Create at least one role in the Roles tab first.</div>
                  )}
                </div>

                {derivedNewAccountRole === "manager" && (
                  <div className="ra-create-user-block ra-create-user-rolemap">
                    <label>Teams</label>
                    <div className="ra-helper-text">Select which teams this manager can view.</div>
                    {loadingDepartments ? (
                      <div className="ra-helper-text">Loading departments...</div>
                    ) : (
                      <details className="ra-multi-select">
                        <summary>
                          {newAccountAllTeams
                            ? "All Teams"
                            : newAccountManagedDepartmentIds.length > 0
                              ? `${newAccountManagedDepartmentIds.length} team(s) selected`
                              : "Select teams"}
                        </summary>
                        <div className="ra-multi-select-options">
                          <label className="ra-option-row">
                            <input
                              type="checkbox"
                              checked={newAccountAllTeams}
                              onChange={(e) => {
                                setNewAccountAllTeams(e.target.checked);
                                if (e.target.checked) setNewAccountManagedDepartmentIds([]);
                              }}
                            />
                            <span>All Teams</span>
                          </label>
                          {departments.map((dept) => (
                            <label key={`create-mgt-${dept._id}`} className="ra-option-row">
                              <input
                                type="checkbox"
                                checked={newAccountAllTeams || newAccountManagedDepartmentIds.includes(dept._id)}
                                onChange={() => {
                                  if (newAccountAllTeams) return;
                                  toggleTemplateSelection(dept._id, setNewAccountManagedDepartmentIds);
                                }}
                                disabled={newAccountAllTeams}
                              />
                              <span>{dept.name}</span>
                            </label>
                          ))}
                          {departments.length === 0 && (
                            <div className="ra-empty">No departments created yet.</div>
                          )}
                        </div>
                      </details>
                    )}
                  </div>
                )}

                {derivedNewAccountRole === "employee" && (
                  <div className="ra-create-user-block ra-create-user-rolemap">
                    <label>Device (Agent)</label>
                    {loadingAgents ? (
                      <div className="ra-helper-text">Loading devices...</div>
                    ) : agents.length > 0 ? (
                      <select
                        value={newAccountAgentId}
                        onChange={(event) => setNewAccountAgentId(event.target.value)}
                        className="ra-select"
                        required
                      >
                        <option value="">Select a device</option>
                        {agents.map((agent) => (
                          <option key={agent.agentId} value={agent.agentId}>
                            {agent.hostname || agent.agentId}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <input
                        value={newAccountAgentId}
                        onChange={(event) => setNewAccountAgentId(event.target.value)}
                        placeholder="No devices found — enter Agent ID manually"
                        required
                      />
                    )}
                    <div className="ra-helper-text">The employee will only see their own device data.</div>
                  </div>
                )}

                {["employee", "manager"].includes(derivedNewAccountRole) ? (
                  <div className="ra-create-user-block ra-scoped-role-notice">
                    <p className="ra-scoped-notice-text">
                      Access for this role is managed by the selected template. No further view permissions needed.
                    </p>
                  </div>
                ) : (
                  <>
                    {selectedCreateViews.includes("manager_dashboard") && (
                      <div className="ra-create-user-block ra-create-user-rolemap">
                        <label>Managed Departments</label>
                        <div className="ra-helper-text">
                          This role includes the Manager Dashboard view. Select the department(s) this account should manage.
                        </div>
                        <div className="ra-role-select-row ra-role-select-stack">
                          <details className="ra-multi-select">
                            <summary>
                              {newAccountManagedDepartmentIds.length > 0
                                ? `${newAccountManagedDepartmentIds.length} department(s) selected`
                                : "Select departments"}
                            </summary>
                            <div className="ra-multi-select-options">
                              {departments.map((dept) => (
                                <label key={`create-dept-${dept._id}`} className="ra-option-row">
                                  <input
                                    type="checkbox"
                                    checked={newAccountManagedDepartmentIds.includes(dept._id)}
                                    onChange={() => toggleTemplateSelection(dept._id, setNewAccountManagedDepartmentIds)}
                                    disabled={loadingDepartments}
                                  />
                                  <span>{dept.name}</span>
                                </label>
                              ))}
                              {departments.length === 0 && <div className="ra-empty">No departments created yet.</div>}
                            </div>
                          </details>
                        </div>
                      </div>
                    )}

                    <div className="ra-create-user-block ra-create-user-preview-wrap">
                      <div className="ra-create-user-preview-grid">
                        <div className="ra-template-preview">
                          <div className="ra-template-head">Selected Roles</div>
                          <div className="ra-view-chips">
                            {selectedRoleTemplates.length === 0 ? (
                              <span className="ra-view-chip muted">No roles selected</span>
                            ) : (
                              selectedRoleTemplates.map((tpl) => (
                                <span key={tpl._id} className="ra-view-chip">{tpl.name}</span>
                              ))
                            )}
                          </div>
                        </div>
                        <div className="ra-template-preview">
                          <div className="ra-template-head">Views from selected role(s)</div>
                          <div className="ra-view-chips">
                            {selectedCreateViews.length === 0 ? (
                              <span className="ra-view-chip muted">No views assigned</span>
                            ) : (
                              selectedCreateViews.map((id) => (
                                <span key={`create-modal-preview-${id}`} className="ra-view-chip">{labelForView(id)}</span>
                              ))
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>

              <div className="ra-views-actions">
                <button type="button" className="ra-secondary-btn" onClick={closeCreateUserPanel}>Cancel</button>
                <button
                  type="submit"
                  className="ra-primary-btn"
                  disabled={creatingAccount || selectedRoleTemplateIds.length === 0}
                >
                  {creatingAccount ? "Creating..." : "Create User"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {editingAccountRole && (
        <div className="ra-modal-overlay" onClick={closeAccountRoleEditor}>
          <div className="ra-modal ra-modal-wide" onClick={(event) => event.stopPropagation()}>
            <div className="ra-modal-header">
              <div><h3>Edit Account</h3><p>{editingAccountRole.email}</p></div>
              <button type="button" className="ra-modal-close" onClick={closeAccountRoleEditor} aria-label="Close edit account modal">×</button>
            </div>

            <div className="ra-modal-scroll ra-modal-body-pad">
              <div className="ra-create-user-block ra-create-user-rolemap">
                <label className="ra-section-label">Role (controls dashboard &amp; data access)</label>
                <div className="ra-helper-text">
                  Required. Select one or more role templates. Picking "Employee" or "Manager" grants that
                  dashboard exclusively; any other selection grants the combined views from the chosen templates.
                </div>
                <details className="ra-multi-select">
                  <summary>{selectedAccountRoleTemplateIds.length > 0 ? `${selectedAccountRoleTemplateIds.length} role(s) selected` : "Select roles"}</summary>
                  <div className="ra-multi-select-options">
                    {roleTemplates.map((tpl) => (
                      <label key={`assign-${tpl._id}`} className="ra-option-row">
                        <input type="checkbox" checked={selectedAccountRoleTemplateIds.includes(tpl._id)} onChange={() => toggleEditRoleTemplate(tpl)} />
                        <span>{tpl.name}</span>
                      </label>
                    ))}
                  </div>
                </details>
              </div>

              {derivedEditAccountRole === "manager" && (
                <div className="ra-create-user-block ra-create-user-rolemap">
                  <label>Teams</label>
                  <div className="ra-helper-text">Select which teams this manager can view.</div>
                  {loadingDepartments ? (
                    <div className="ra-helper-text">Loading departments...</div>
                  ) : (
                    <details className="ra-multi-select">
                      <summary>
                        {editAccountAllTeams
                          ? "All Teams"
                          : editAccountManagedDepartmentIds.length > 0
                            ? `${editAccountManagedDepartmentIds.length} team(s) selected`
                            : "Select teams"}
                      </summary>
                      <div className="ra-multi-select-options">
                        <label className="ra-option-row">
                          <input
                            type="checkbox"
                            checked={editAccountAllTeams}
                            onChange={(e) => {
                              setEditAccountAllTeams(e.target.checked);
                              if (e.target.checked) setEditAccountManagedDepartmentIds([]);
                            }}
                          />
                          <span>All Teams</span>
                        </label>
                        {departments.map((dept) => (
                          <label key={`edit-mgt-${dept._id}`} className="ra-option-row">
                            <input
                              type="checkbox"
                              checked={editAccountAllTeams || editAccountManagedDepartmentIds.includes(String(dept._id))}
                              onChange={() => {
                                if (editAccountAllTeams) return;
                                toggleTemplateSelection(String(dept._id), setEditAccountManagedDepartmentIds);
                              }}
                              disabled={editAccountAllTeams}
                            />
                            <span>{dept.name}</span>
                          </label>
                        ))}
                        {departments.length === 0 && (
                          <div className="ra-empty">No departments created yet.</div>
                        )}
                      </div>
                    </details>
                  )}
                </div>
              )}

              {derivedEditAccountRole === "employee" && (
                <div className="ra-create-user-block ra-create-user-rolemap">
                  <label>Device (Agent)</label>
                  {loadingAgents ? (
                    <div className="ra-helper-text">Loading devices...</div>
                  ) : agents.length > 0 ? (
                    <select
                      value={editAccountAgentId}
                      onChange={(event) => setEditAccountAgentId(event.target.value)}
                      className="ra-select"
                      aria-label="Agent"
                    >
                      <option value="">Select a device</option>
                      {agents.map((agent) => (
                        <option key={agent.agentId} value={agent.agentId}>
                          {agent.hostname || agent.agentId}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      value={editAccountAgentId}
                      onChange={(event) => setEditAccountAgentId(event.target.value)}
                      placeholder="No devices found — enter Agent ID manually"
                      aria-label="Agent ID"
                    />
                  )}
                  <div className="ra-helper-text">The employee will only see their own device data.</div>
                </div>
              )}

              {!["employee", "manager"].includes(derivedEditAccountRole) && (
                <div className="ra-create-user-block">
                  <div className="ra-template-preview">
                    <div className="ra-template-head">Views from selected role(s)</div>
                    <div className="ra-view-chips">
                      {selectedAccountViews.length === 0
                        ? <span className="ra-view-chip muted">No views assigned</span>
                        : selectedAccountViews.map((id) => <span key={`assign-preview-${id}`} className="ra-view-chip">{labelForView(id)}</span>)}
                    </div>
                  </div>
                </div>
              )}

              {!["employee", "manager"].includes(derivedEditAccountRole) && selectedAccountViews.includes("manager_dashboard") && (
                <div className="ra-create-user-block ra-create-user-rolemap">
                  <label>Managed Departments</label>
                  <div className="ra-helper-text">
                    This role includes the Manager Dashboard view. Select the department(s) this account should manage.
                  </div>
                  <details className="ra-multi-select">
                    <summary>
                      {editAccountManagedDepartmentIds.length > 0
                        ? `${editAccountManagedDepartmentIds.length} department(s) selected`
                        : "Select departments"}
                    </summary>
                    <div className="ra-multi-select-options">
                      {departments.map((dept) => (
                        <label key={`edit-dept-${dept._id}`} className="ra-option-row">
                          <input
                            type="checkbox"
                            checked={editAccountManagedDepartmentIds.includes(dept._id)}
                            onChange={() => toggleTemplateSelection(dept._id, setEditAccountManagedDepartmentIds)}
                            disabled={loadingDepartments}
                          />
                          <span>{dept.name}</span>
                        </label>
                      ))}
                      {departments.length === 0 && <div className="ra-empty">No departments created yet.</div>}
                    </div>
                  </details>
                </div>
              )}

              {["employee", "manager"].includes(derivedEditAccountRole) && (
                <div className="ra-create-user-block ra-scoped-role-notice">
                  <p className="ra-scoped-notice-text">Access for this role is managed by the selected template. No further view permissions needed.</p>
                </div>
              )}

              <div className="ra-create-user-block ra-create-user-rolemap">
                <label>Reset Password (optional)</label>
                <div className="ra-password-input-wrapper">
                  <input
                    type={showEditAccountPassword ? "text" : "password"}
                    value={editAccountPassword}
                    onChange={(event) => setEditAccountPassword(event.target.value)}
                    placeholder="Leave blank to keep current password"
                    minLength={8}
                  />
                  <button
                    type="button"
                    className="ra-password-toggle-btn"
                    onClick={() => setShowEditAccountPassword(!showEditAccountPassword)}
                    tabIndex={-1}
                  >
                    {showEditAccountPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                <div className="ra-helper-text">
                  Set a new password for this account. At least 8 characters with a mix of uppercase, lowercase, numbers, and symbols.
                </div>
              </div>
            </div>

            <div className="ra-views-actions">
              <button type="button" className="ra-secondary-btn" onClick={closeAccountRoleEditor}>Cancel</button>
              <button
                type="button"
                className="ra-primary-btn"
                onClick={saveAccountRole}
                disabled={savingAccountRoleId === editingAccountRole._id}
              >
                {savingAccountRoleId === editingAccountRole._id ? "Saving..." : "Save Account"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function hasManageAccountsAccess() {
  try {
    const raw = JSON.parse(sessionStorage.getItem("allowedViews") || "[]");
    return Array.isArray(raw) && raw.includes("productivity_config");
  } catch {
    return false;
  }
}

function RoleAccounts() {
  const userRole = getRole();
  // The seeded "download@<domain>" login is role:"client" (so it can authenticate the
  // same way as the tenant owner) but it is NOT the owner — without this check it would
  // render the full tenant account-management panel just because it shares that role.
  // It has no profile section at all: nothing useful to show, so render nothing.
  const displayRole = sessionStorage.getItem("displayRole") || "";
  const email = (sessionStorage.getItem("email") || "").toLowerCase();
  const isDownloadOnlyAccount = displayRole === "Download Account" || email.startsWith("download@");
  if (isDownloadOnlyAccount) return <Navigate to="/download" replace />;

  // The tenant owner and platform admin get the full Manage Accounts page (Profile is
  // just its default tab, near Logout in the TopNav dropdown). A custom role template
  // granted "Configuration Settings" gets the same full page — not just its own
  // profile — which is what the Productivity Config "Accounts" link relies on.
  if (userRole !== "admin" && userRole !== "client" && !hasManageAccountsAccess()) {
    return <MyProfileView />;
  }
  return <TenantAccountsManager />;
}

export default RoleAccounts;
